import { useEffect, useState, useContext, useCallback } from "react";
import { API, ShareAPI } from "../../../API";
import { useTranslation } from 'react-i18next';
import { BreakdownsContext } from "./contexts/BreakdownsContext";
import { BreakdownsContextType } from "./contexts/BreakdownsContextTypes";
import { BreakdownsContainer } from "../../../components/BreakdownsContainer/BreakdownsContainer";
import { LoaderSmall } from "../../../components/LoaderSmall/LoaderSmall";
import {
  HorizontalBarChart,
  HorizontalBarDatum,
  OTHER_BAR_COLOR,
} from "../../../components/HorizontalBarChart/HorizontalBarChart";
import styles from '../NarrativePage.module.scss';
import { showRequestError } from '../../../utils/requestError';
import { narrativeActorsUrl } from '../../../utils/narrativeActors';

interface Country {
  country_id: number;
  country_name: string;
  actors_count: number;
}

interface CountriesData {
  objects: Country[];
}

interface CountriesBarChartProps {
  narrative: { id: number };
  isShare?: boolean;
  title?: string;
  isClickable?: boolean;
}

export const CountriesBarChart = ({
  narrative,
  isShare = false,
  title,
  isClickable = true,
}: CountriesBarChartProps) => {
  const { t } = useTranslation();
  const [countriesData, setCountriesData] = useState<CountriesData | null>(null);

  const { openModal, setCountriesFilter, setCurrentWidgetName, setModalTitle } = useContext(BreakdownsContext) as BreakdownsContextType;

  const api = isShare ? ShareAPI : API;
  const getNarrativeCountries = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `countries_breakdown`);
    urlParams.set('top', '206');
    const url = narrativeActorsUrl(narrative.id, 'countries', urlParams);
    api.fetch('GET', url).then(
      (data: CountriesData) => setCountriesData(data)
    ).catch((e: Error) => showRequestError(e, t('Countries')));
  }, [api, narrative.id]);

  useEffect(() => {
    getNarrativeCountries();
  }, [getNarrativeCountries]);

  const topCountries = countriesData?.objects?.slice(0, 10);
  const otherCountries = countriesData?.objects?.slice(10);
  const otherCountriesCount = otherCountries?.reduce((accumulator, currentValue) => accumulator + currentValue.actors_count, 0) || 0;

  const chartData: HorizontalBarDatum[] | null = countriesData?.objects?.length && topCountries
    ? topCountries.map((country) => ({
        name: country.country_name,
        value: country.actors_count,
      }))
    : null;

  if (chartData && otherCountriesCount > 0) {
    chartData.push({ name: t('Others'), value: otherCountriesCount, color: OTHER_BAR_COLOR });
  }

  const onSelect = (index: number | null) => {
    if (index === null || !topCountries) return;
    const isOthers = index >= topCountries.length;
    if (isOthers) {
      const countriesOptionList = otherCountries?.map(c => ({ value: c.country_id, label: c.country_name }));
      setCountriesFilter(countriesOptionList);
      setCurrentWidgetName('countries_breakdown');
      setModalTitle(t('Others'));
      openModal();
      return;
    }
    const country = topCountries[index];
    if (!country) return;
    setCountriesFilter([{ value: country.country_id, label: country.country_name }]);
    setCurrentWidgetName('countries_breakdown');
    setModalTitle(country.country_name);
    openModal();
  };

  return (
    <BreakdownsContainer
      title={title || 'Actors by countries'}
      onClick={() => openModal()}
      details={false}
      flexGrow={3}
    >
      {countriesData
        ? (chartData && chartData.length > 0
          ? <div className={styles.chartWrapper} translate="no">
              <HorizontalBarChart
                chartData={chartData}
                isClickable={isClickable}
                onSelect={onSelect}
                seriesName={t('Actors')}
              />
            </div>
          : <div className={styles.noDataBreakdowns}>{t('There is no data to display')}</div>
        )
        : <div className={styles.widgetLoader}><LoaderSmall /></div>
      }
    </BreakdownsContainer>
  );
};
